import qs from 'qs'
import trimStart from 'lodash/trimStart'
import omit from 'lodash/omit'
import isEmpty from 'lodash/isEmpty'

import {awsFetchRest} from 'utils/asyncUtils'
import {getLang, parseJWT, randomString, getLoginHint} from 'utils/stringUtils'
import {ID_LIST, POST_REDIRECT_ACTION, PROFILE_TABS} from 'const'
import {getDeviceType} from 'utils/browserUtils'
import {isLocal} from 'utils/envUtils'
import WebAuthnService from './WebAuthnService'

const EMAIL_ONLY_LOGIN_CASE = 'email_only'

class ProfileAuthService {
  auth0
  static instance = null

  constructor() {
    if (ProfileAuthService.instance) {
      // eslint-disable-next-line no-constructor-return
      return ProfileAuthService.instance
    }
    ProfileAuthService.instance = this
  }

  async init(clientId = process.env.REACT_APP_OIDC_CLIENT, scope = 'email profile:manage') {
    const {default: createAuth0Client} = await import(/* webpackChunkName: "auth0-spa-js" */ '@auth0/auth0-spa-js')
    this.auth0 = await createAuth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      client_id: clientId,
      user_source: 'onelog-profilepage',
      redirect_uri: this.getDefaultRedirectUri(),
      cacheLocation: isLocal() ? 'localstorage' : 'memory',
      advancedOptions: {
        defaultScope: scope,
      },
      login_case: EMAIL_ONLY_LOGIN_CASE,
    })
  }

  async authorize(options) {
    await this.auth0?.loginWithRedirect(options)
  }

  async startAuthorizeForWebAuthnSetup(email) {
    // 1. save current route to restore after back to profile app
    const appState = randomString(32)
    this.storePostRedirectAction(appState)

    // 2. remove current sub from cookie to trigger web-authn-setup again
    WebAuthnService.removeAccountFromCookie(await this.getUserId())

    // 3. trigger authorization
    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      lang: getLang(window.location.search),
      prompt: 'web-authn-setup',
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForCredentialRemove(email, credential, modal) {
    const appState = randomString(32)
    this.storePostRedirectAction(appState, {credential, modal})

    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForChangeEmail(email) {
    const appState = randomString(32)
    const redirectTo = `change-email${window.location.search}`

    this.storePostRedirectAction(appState, {redirectTo})

    await this.auth0?.loginWithRedirect({
      max_age: 300,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForDeletionPage(email) {
    const appState = randomString(32)
    const redirectTo = `delete-user${window.location.search}`

    this.storePostRedirectAction(appState, {redirectTo})

    await this.auth0?.loginWithRedirect({
      max_age: 300,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForMfaDeactivation(email, type, modal) {
    const appState = randomString(32)
    this.storePostRedirectAction(appState, {type, modal})

    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForMfaActivation(email, mfaType) {
    const appState = randomString(32)
    this.storePostRedirectAction(appState)

    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      prompt: 'setup-mfa',
      mfa_type: mfaType,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForGenerationCodes(email, modal) {
    const appState = randomString(32)
    this.storePostRedirectAction(appState, {modal})

    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startAuthorizeForIdentityRemove(email, identity, modal) {
    const appState = randomString(32)

    const pathname = window.location.pathname.slice(1) || PROFILE_TABS.global
    const queryString = this.buildPostRedirectActionQueryString()
    const redirectTo = pathname + queryString + window.location.hash

    this.storePostRedirectAction(appState, {identity, modal, redirectTo})

    await this.auth0?.loginWithRedirect({
      max_age: 600,
      login_hint: email,
      lang: getLang(window.location.search),
      enforce_login_after_cancel: false,
      appState,
    })
  }

  async startDefaultAuthorize() {
    const loginHint = getLoginHint(window.location.search)
    const appState = randomString(32)
    this.storePostRedirectAction(appState)

    const brandName = trimStart(window.location.pathname, '/')
    const doesBrandExist = ID_LIST[brandName]
    await this.auth0?.loginWithRedirect({
      lang: getLang(window.location.search),
      brand_theme: doesBrandExist ? brandName : undefined,
      ...(loginHint ? {login_hint: loginHint, prompt: 'login'} : {}),
      appState,
    })
  }

  buildPostRedirectActionQueryString() {
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })

    /*
      login_hint is one time action. remove it
     */
    delete params.login_hint

    /*
      `lang` persists in a cookie and is shared across all OneLog subdomains,
      hence we don't need to persist it in URL, because it will always overwrite
      the value in the cookie
     */
    delete params.lang

    return qs.stringify(params, {addQueryPrefix: true})
  }

  storePostRedirectAction(state, extraParams = {}) {
    const queryString = this.buildPostRedirectActionQueryString()
    const returnTo = window.location.pathname.slice(1) + queryString + window.location.hash
    if (extraParams.redirectTo || returnTo) {
      const payload = {
        ...(extraParams.redirectTo && {redirectTo: extraParams.redirectTo}),
        ...(returnTo && {returnTo}),
        ...extraParams,
      }

      Object.keys(sessionStorage)
        .filter(key => key.startsWith(POST_REDIRECT_ACTION))
        .forEach(key => sessionStorage.removeItem(key))
      sessionStorage.setItem(`${POST_REDIRECT_ACTION}${state}`, JSON.stringify(payload))
    }
  }

  restorePostRedirectAction = (state, hasError) => {
    const payload = sessionStorage.getItem(`${POST_REDIRECT_ACTION}${state}`)
    if (payload) {
      sessionStorage.removeItem(`${POST_REDIRECT_ACTION}${state}`)

      // no errors from OIDC + contains at least one another property except return/redirect URLs
      if (!hasError && !isEmpty(omit(payload, ['redirectTo', 'returnTo']))) {
        sessionStorage.setItem(POST_REDIRECT_ACTION, payload)
      }

      return JSON.parse(payload)
    }

    return {}
  }

  getDefaultRedirectUri = () => `${window.location.origin}/callback`

  handleAuthentication = async () => {
    try {
      const result = await this.auth0?.handleRedirectCallback()
      return {appState: result?.appState, hasError: false}
    } catch (error) {
      return {appState: error?.appState, hasError: true}
    }
  }

  logout = () => {
    this.storePostRedirectAction('postLogoutPayload')
    const query = qs.stringify({lang: getLang(window.location.search) || 'de'})
    this.auth0.logout({returnTo: `${window.location.origin}?${query}`})
  }

  clearSession = () => {
    this.auth0.logout({localOnly: true})
  }

  getAccessToken = async () => {
    try {
      return await this.auth0?.getTokenSilently()
    } catch (e) {
      this.startDefaultAuthorize()
    }
  }

  doesSessionExist = async () => {
    try {
      const token = await this.auth0?.getTokenSilently()
      return !!token
    } catch (e) {
      return false
    }
  }

  forgetPassword = (email, captchaToken) => {
    const registrationChannel = getDeviceType()
    const lang = getLang(window.location.search)
    return awsFetchRest(`${process.env.REACT_APP_IDENTITY_SERVICE_URL}/native/password-reset/request`, 'POST', {
      email,
      registrationChannel,
      lang,
      captchaToken,
    })
  }

  getUserId = async () => {
    // TODO: [Sentry] null access
    const token = await this.getAccessToken()
    return parseJWT(token).sub
  }

  getUserEmail = async () => {
    const user = await this.auth0?.getUser()
    return user && user.email
  }

  isTokenAuthTimeValid = async (time = 600 /* 10min */) => {
    const accessToken = await this.getAccessToken()
    if (accessToken) {
      const payload = parseJWT(accessToken)
      return Date.now() / 1000 < payload.auth_time + time
    }
    return false
  }
}

export default ProfileAuthService
